

import { Component, Vue, Watch, Prop, Ref } from "vue-property-decorator"

import { AdvancedSearchFilter, DataType, TimeZone } from "@/types"
import { CaseColumn } from "@/types/caseColumns"
import { Operator, OperatorLabel, operatorLabels, operatorsByFieldType } from "@/types/insights/operators"

import moment from "moment"

import api from "@/services/api"
import vSelect from "vue-select"
import ParameterValue from "@/components/Controls/ParameterValue.vue"
import DataSourceSelect from "@/components/Fields/DataSourceSelect.vue"
import { SearchClauseParameters } from "@/types/view"
import { UserRole } from "@/types/caseUser"
import { State } from "vuex-class"
import { BDropdown } from "bootstrap-vue"
import { DateRangeOptions } from "@/types/insights/dateRange"

@Component({
	components: {
		"data-select": DataSourceSelect,
		"v-select": vSelect,
		"parameter-value": ParameterValue,
		"column-filters": () => import("./ColumnFilters.vue")
	}
})
export default class SearchFilter extends Vue {
	@Ref() dropdown!: BDropdown

	@Prop()
	value!: AdvancedSearchFilter;

	@Prop()
	allCaseFields!: CaseColumn[];

	@Prop({ default: () => [] })
	public filterColumnGroups!: CaseColumn[][]

	@Prop()
	field!: CaseColumn;

	@State displayTimezone!: TimeZone | null

	filterValues: any[] = [];
	filterSelectedValue: any = null;
	operator = Operator; // use enum value in markup

	get filterOperators (): OperatorLabel[] {
		if (!this.value || !this.value.columnDataType) {
			return []
		}

		let fieldTypeOperators: Operator[] | null = null

		if (this.isSubroleFilterSelected) { // handle subrole filtering of user-role if applied
			fieldTypeOperators = operatorsByFieldType["subrole"]
		} else {
			fieldTypeOperators = this.field.operators ?? operatorsByFieldType[this.value.columnDataType]
		}
		return operatorLabels.filter(o => fieldTypeOperators!.indexOf(o.operator) > -1)
	}

	get filterLabel (): string {
		if (!this.value) return ""

		if (this.field.fieldDataType === DataType.dateTime) {
			if (this.value.operator === Operator.DateRange) {
				switch (this.value.value) {
					case DateRangeOptions.PreviousWeek:
						return "Previous Week"
					case DateRangeOptions.NextWeek:
						return "Next Week"
					case DateRangeOptions.CurrentWeek:
						return "This Week"
					case DateRangeOptions.CurrentMonth:
						const thisMonth = moment().startOf("month").toDate()
						return "This Month of " + thisMonth.toLocaleString("en-us", { month: "long" })
					case DateRangeOptions.CurrentQuarter:
						return "This Quarter"
					case DateRangeOptions.YearToDate:
						const year = new Date().getFullYear()
						return "In " + year
					case DateRangeOptions.WeekNumber:
						return "Week #" + (this.value.searchClauseParameters?.weekNumber ?? "")
					case DateRangeOptions.MonthRange:
						const shortDateFormattingOptions = { month: "short", year: "numeric", timeZone: "UTC" }
						return `Between
							${this.value.searchClauseParameters?.startMonthRange?.toLocaleString("en-us", shortDateFormattingOptions)}
							and ${this.value.searchClauseParameters?.endMonthRange?.toLocaleString("en-us", shortDateFormattingOptions)}`
					case DateRangeOptions.DateRange:
						const params = this.value.searchClauseParameters
						return params ? `Between ${params.startDate?.toLocaleDateString()} and ${params.endDate?.toLocaleDateString()}`
							: ""
					default:
						return "Error: Invalid Date Range"
				}
			} else if (this.value.dynamicValue) {
				switch (this.value.value) {
					case "CurrentWeek":
						return "This Week"
					case "CurrentMonth":
						const thisMonth = moment().startOf("month").toDate()
						return "This Month (of " + thisMonth.toLocaleString("en-us", { month: "long" }) + ")"
					case "CurrentQuarter":
						return "This Quarter"
					case "NextWeek":
						return "Next Week"
					case "PreviousWeek":
						return "Last Week"
				}

				if (!isNaN(parseInt(this.value.value ?? "NaN"))) {
					const val = parseInt(this.value.value!)
					const absVal = Math.abs(val)
					const isPast = val < 0
					if (val >= -1 && val <= 1) {
						return isPast ? "Yesterday"
							: val === 0 ? "Today"
								: "Tomorrow"
					}
					return this.value.value?.toString().replace("-", "") + " days " + (isPast ? "ago" : "from now")
				} else if (this.value.value === null || this.value.value === "") {
					return "Relative Days"
				} else {
					return "Error: Invalid Date Range"
				}
			} else {
				return this.value.value ? moment(this.value.value).toDate().toLocaleDateString("en-us", { timeZone: this.displayTimezone?.iana }) : ""
			}
		} else if (this.field.fieldDataType === DataType.timeSpentAsValue) {
			const params = this.value.searchClauseParameters
			return params
				? `${params.timeSpentClause.searchLabel} for ${params.timeSpentClause.hoursSpent} hours`
				: ""
		} else {
			return this.value.label
				? this.value.label
				: this.value.value
					? this.value.value
					: ""
		}
	}

	public async mounted () {
		if (this.value.columnValues) {
			this.filterValues = await this.value.columnValues(api)
			this.filterSelectedValue = this.filterValues.find(v => v.id === this.value.value)
		}
	}

	private updateOperator (newOperator: Operator) {
		if (newOperator === Operator.IsSet || newOperator === Operator.NotSet) {
			this.value.value = ""
			this.value.label = null
		}

		const newOperatorLabel = operatorLabels.find(o => o.operator === newOperator)
		if (newOperatorLabel) {
			this.value.operatorLabel = newOperatorLabel.label
		} else {
			this.value.operatorLabel = ""
		}

		this.value.value = null
		this.value.label = ""
	}

	private setFilterValue (newValue: any) {
		if (newValue) {
			this.value.value = newValue.id
			this.value.label = this.value.columnListLabel ? newValue[this.value.columnListLabel] : newValue.label
		} else {
			this.value.value = null
			this.value.label = ""
		}
	}

	public get isUserRoleFieldWithSubroles () : boolean {
		return this.field.fieldDataType === DataType.userRole &&
			this.field.fieldExtensions && this.field.fieldExtensions!.hasSubroles
	}

	public get userFieldsWithSubroles (): CaseColumn[] {
		return [
			...(this.field.fieldExtensions && this.field.fieldExtensions.subroles
				? this.field.fieldExtensions.subroles.map((subRole: UserRole) => {
					var column: CaseColumn = {
						key: subRole.title,
						label: "Subrole: " + subRole.title,
						dataType: "subrole",
						fieldDataType: DataType.subrole,
						dynamicField: this.field.dynamicField,
						fieldId: this.field.fieldId
					}
					return column
				})
				: []
			)
		]
	}

	get isSubroleFilterSelected (): boolean {
		if (!this.value || !this.value!.userFieldData) return false

		return this.value.userFieldData.dataType === "subrole"
	}

	get selectedSubroleLabel (): string | null {
		if (this.isSubroleFilterSelected) {
			return this.value!.userFieldData!.key + " role"
		}

		return null
	}

	public setUserRole (caseColumn: CaseColumn): void {
		if (!caseColumn) {
			return
		}

		if ((this.value!.columnDataType !== caseColumn.dataType) && !(this.value!.userFieldData &&
				this.value.userFieldData!.dataType === caseColumn.dataType)) {
			this.value.value = null
			this.value.label = ""
		}

		if (!this.filterOperators.find((ol: OperatorLabel) => ol.operator === this.value!.operator)) {
			this.value!.operator = Operator.Contains
			this.updateOperator(Operator.Contains)
		}
	}

	private removeFilter () {
		this.$emit("removeFilter")
	}

	searchClauseParametersChanged (clause: any, searchClauseParameters: SearchClauseParameters) {
		Vue.set(clause, "searchClauseParameters", searchClauseParameters)
	}

	public open () {
		this.dropdown.show()
	}
}

